import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, ArrowRight, Zap, LogOut, LayoutDashboard, HeartPulse, Receipt, Users,
  ShieldCheck, Gauge, Link2, CheckCircle2, AlertTriangle, Search, Download, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { startSocialPulseLogin } from "@/lib/oidc";
import {
  getOverview, getPortalLeads, runHealthCheck, indiaBilling,
  getCountryPackages, rzpCreateOrder, rzpVerify, rzpSubscribe, rzpSubscriptionVerify,
  loadRazorpayCheckout, indiaInvoicePdfUrl,
} from "@/lib/api";

const INR = (n) => (typeof n === "number" ? "\u20b9" + n.toLocaleString("en-IN") : n);

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function IndiaPortal() {
  const { user, login, register, logout } = useAuth();
  useEffect(() => { document.title = "WebsiteTech India · Customer Dashboard"; }, []);

  if (user === undefined) return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={26} /></div>;
  if (!user) return <IndiaAuth login={login} register={register} />;
  return <IndiaDashboard user={user} logout={logout} />;
}

function IndiaAuth({ login, register }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full bg-white border border-slate-200 rounded-lg px-3.5 py-3 text-slate-900 outline-none focus:border-brand text-sm";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register({ name: form.name, email: form.email, password: form.password, company: form.company, country_id: "in" });
      toast.success("Welcome to your dashboard");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <section className="bg-gradient-to-br from-[#0B203E] to-[#071428] min-h-[80vh] py-16 flex items-center" data-testid="india-portal-auth">
      <div className="max-w-md mx-auto px-4 w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl soft-shadow-lg p-8">
          <h1 className="font-heading text-2xl font-bold text-slate-900">{mode === "login" ? "Customer Login" : "Create your account"}</h1>
          <p className="text-slate-500 text-sm mt-1">Your WebsiteTech India dashboard — website health, ₹ invoices, subscription & enquiries.</p>
          <form onSubmit={submit} className="space-y-3 mt-6">
            {mode === "register" && (
              <>
                <input placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} data-testid="india-auth-name" required />
                <input placeholder="Business (optional)" value={form.company} onChange={set("company")} className={inputCls} data-testid="india-auth-company" />
              </>
            )}
            <input type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="india-auth-email" required />
            <input type="password" placeholder="Password" value={form.password} onChange={set("password")} className={inputCls} data-testid="india-auth-password" required />
            <button type="submit" disabled={loading} data-testid="india-auth-submit" className="w-full bg-brand text-white font-semibold rounded-xl py-3 flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <>{mode === "login" ? "Log In" : "Create Account"} <ArrowRight size={16} /></>}
            </button>
          </form>
          <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-sm text-brand mt-4 hover:underline" data-testid="india-auth-toggle">
            {mode === "login" ? "New here? Create an account" : "Already have an account? Log in"}
          </button>
          <div className="flex items-center gap-3 my-5"><span className="h-px bg-slate-200 flex-1" /><span className="text-xs text-slate-400">or</span><span className="h-px bg-slate-200 flex-1" /></div>
          <button type="button" onClick={async () => { try { await startSocialPulseLogin(); } catch { toast.error("Could not start SocialPulse sign-in."); } }}
            data-testid="india-sso-socialpulse"
            className="relative w-full rounded-lg p-[2px] bg-gradient-to-r from-[#A855F7] to-[#4ADE80] hover:shadow-[0_0_18px_rgba(168,85,247,0.28)] transition-shadow group">
            <span className="flex items-center justify-center gap-2 w-full py-3 rounded-[7px] bg-white text-slate-800 font-semibold group-hover:bg-slate-50 transition-colors">
              <Zap size={16} className="text-[#A855F7]" /> Continue with SocialPulse
            </span>
          </button>
        </motion.div>
      </div>
    </section>
  );
}

function IndiaDashboard({ user, logout }) {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (t) => setParams({ tab: t });
  const [overview, setOverview] = useState(null);
  const [leads, setLeads] = useState(null);
  const [billing, setBilling] = useState(null);

  const load = useCallback(() => {
    getOverview().then(setOverview).catch(() => setOverview({}));
    getPortalLeads().then(setLeads).catch(() => setLeads([]));
    indiaBilling().then(setBilling).catch(() => setBilling(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const TabBtn = ({ id, icon: Icon, children }) => (
    <button onClick={() => setTab(id)} data-testid={`india-portal-tab-${id}`}
      className={`px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 ${tab === id ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
      <Icon size={15} /> {children}
    </button>
  );

  return (
    <div className="bg-slate-50 min-h-[80vh]" data-testid="india-portal-dashboard">
      <div className="bg-[#0B203E] text-white py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold">Welcome, {user.name?.split(" ")[0] || "there"}</h1>
            <p className="text-blue-100/70 text-sm">Your WebsiteTech India customer dashboard · ₹ INR</p>
          </div>
          <button onClick={logout} data-testid="india-portal-logout" className="ml-auto text-xs bg-white/10 border border-white/20 rounded-lg px-3 py-2 hover:bg-white/20 inline-flex items-center gap-1.5"><LogOut size={13} /> Log out</button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-6">
          <TabBtn id="overview" icon={LayoutDashboard}>Overview</TabBtn>
          <TabBtn id="health" icon={HeartPulse}>Website Health</TabBtn>
          <TabBtn id="billing" icon={Receipt}>Billing (₹)</TabBtn>
          <TabBtn id="enquiries" icon={Users}>Enquiries {leads ? `(${leads.length})` : ""}</TabBtn>
        </div>

        {tab === "overview" && <Overview overview={overview} billing={billing} leads={leads} setTab={setTab} />}
        {tab === "health" && <HealthTab websites={overview?.site_list} />}
        {tab === "billing" && <BillingTab billing={billing} user={user} onPaid={load} />}
        {tab === "enquiries" && <EnquiriesTab leads={leads} />}
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 card-shadow">
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className="font-heading text-2xl font-extrabold text-slate-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function Overview({ overview, billing, leads, setTab }) {
  if (!overview) return <Loader2 className="animate-spin text-brand mx-auto" size={24} />;
  return (
    <div className="space-y-6" data-testid="india-overview">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Websites" value={overview.websites ?? 0} />
        <Stat label="Avg score" value={overview.avg_score ?? "—"} sub="WebsiteTech Score" />
        <Stat label="Open enquiries" value={overview.open_leads ?? (leads?.length ?? 0)} />
        <Stat label="Total paid" value={billing ? INR(billing.total_paid) : "—"} sub="last 6 invoices" />
      </div>
      {billing?.subscription && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 card-shadow">
          <p className="text-xs uppercase tracking-wide text-slate-400">Current subscription</p>
          <div className="flex items-end justify-between mt-1">
            <div>
              <p className="font-heading text-lg font-bold text-slate-900">{billing.subscription.name}</p>
              <p className="text-sm text-slate-500">Renews on {billing.subscription.renews_on}</p>
            </div>
            <p className="font-heading text-2xl font-extrabold text-brand">{INR(billing.subscription.amount)}<span className="text-sm text-slate-400">/mo</span></p>
          </div>
          <button onClick={() => setTab("billing")} className="text-sm text-brand font-semibold mt-3 inline-flex items-center gap-1 hover:gap-2 transition-all">View invoices <ArrowRight size={14} /></button>
        </div>
      )}
    </div>
  );
}

function HealthTab({ websites }) {
  const [url, setUrl] = useState(websites?.[0]?.url || websites?.[0]?.domain || "");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");

  const run = async (e) => {
    e?.preventDefault();
    if (!url.trim()) return;
    setLoading(true); setErr(""); setRes(null);
    try { setRes(await runHealthCheck(url.trim())); }
    catch { setErr("Couldn't run the health check. Please check the URL."); }
    setLoading(false);
  };

  const Row = ({ icon: Icon, label, ok, value }) => (
    <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${ok ? "bg-emerald-50" : "bg-amber-50"}`}>
        <Icon size={17} className={ok ? "text-emerald-600" : "text-amber-600"} />
      </div>
      <div className="flex-1"><p className="text-sm font-semibold text-slate-800">{label}</p><p className="text-xs text-slate-400">{value}</p></div>
      {ok ? <CheckCircle2 size={18} className="text-emerald-500" /> : <AlertTriangle size={18} className="text-amber-500" />}
    </div>
  );

  return (
    <div data-testid="india-health">
      <form onSubmit={run} className="flex flex-col sm:flex-row gap-3 mb-6">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="yourbusiness.in" data-testid="india-health-input"
          className="flex-1 bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-900 outline-none focus:border-brand text-sm" />
        <button type="submit" disabled={loading} data-testid="india-health-run" className="bg-brand text-white font-semibold rounded-xl px-6 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="animate-spin" size={18} /> : <><Search size={16} /> Run health check</>}
        </button>
      </form>
      {err && <p className="text-rose-500 text-sm mb-4">{err}</p>}
      {res && (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="india-health-result">
          <Row icon={ShieldCheck} label="HTTPS / Uptime" ok={res.up && res.https} value={res.up ? `Online · HTTP ${res.status_code || res.status || 200}` : "Unreachable"} />
          <Row icon={ShieldCheck} label="SSL certificate" ok={(res.ssl_days_left ?? 0) > 0} value={res.ssl_days_left != null ? `${res.ssl_days_left} days left` : "Not detected"} />
          <Row icon={Gauge} label="Page speed" ok={(res.load_seconds ?? 9) < 3} value={res.load_seconds != null ? `${res.load_seconds}s load time` : "—"} />
          <Row icon={Link2} label="Broken links" ok={(res.broken_links?.length ?? 0) === 0} value={`${res.broken_links?.length ?? 0} broken link(s)`} />
        </div>
      )}
      {!res && !loading && <p className="text-slate-500 text-sm">Run an on-demand health check on any of your websites — uptime, SSL, speed and broken links.</p>}
    </div>
  );
}

function BillingTab({ billing, user, onPaid }) {
  const [plans, setPlans] = useState(null);
  const [paying, setPaying] = useState("");

  useEffect(() => {
    getCountryPackages("in").then((d) => {
      const list = d.packages || d || [];
      setPlans(list.filter((p) => p.price_unit === "month").slice(0, 3));
    }).catch(() => setPlans([]));
  }, []);

  const pay = async (plan) => {
    setPaying(plan.id);
    try {
      const ok = await loadRazorpayCheckout();
      if (!ok || !window.Razorpay) { toast.error("Could not load the payment window."); setPaying(""); return; }
      const order = await rzpCreateOrder({ package_id: plan.id });
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency,
        name: order.name, description: order.description, order_id: order.rp_order_id,
        prefill: order.prefill, theme: { color: "#0B203E" },
        handler: async (resp) => {
          try {
            await rzpVerify({
              order_ref: order.order_ref,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful — invoice generated 🎉");
            onPaid && onPaid();
          } catch (e) { toast.error(e?.response?.data?.detail || "Payment verification failed."); }
        },
        modal: { ondismiss: () => setPaying("") },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setPaying(""); });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment.");
    }
    setPaying("");
  };

  const subscribe = async (plan) => {
    setPaying(plan.id);
    try {
      const ok = await loadRazorpayCheckout();
      if (!ok || !window.Razorpay) { toast.error("Could not load the payment window."); setPaying(""); return; }
      const sub = await rzpSubscribe({ package_id: plan.id });
      const rzp = new window.Razorpay({
        key: sub.key_id, subscription_id: sub.subscription_id,
        name: sub.name, description: sub.description, prefill: sub.prefill, theme: { color: "#0B203E" },
        handler: async (resp) => {
          try {
            await rzpSubscriptionVerify({
              sub_ref: sub.sub_ref,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Auto-pay active — you're subscribed 🎉");
            onPaid && onPaid();
          } catch (e) { toast.error(e?.response?.data?.detail || "Subscription verification failed."); }
        },
        modal: { ondismiss: () => setPaying("") },
      });
      rzp.on("payment.failed", () => { toast.error("Subscription setup failed. Please try again."); setPaying(""); });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the subscription.");
    }
    setPaying("");
  };

  const downloadInvoice = async (inv) => {
    if (!inv.real) { toast.info("This is a sample invoice. Pay a plan to get a real GST invoice PDF."); return; }
    try { const url = await indiaInvoicePdfUrl(inv.id); window.open(url, "_blank"); }
    catch { toast.error("Couldn't open the invoice PDF."); }
  };

  if (billing === null) return <Loader2 className="animate-spin text-brand mx-auto" size={24} />;
  const { invoices = [], subscription, gst_note } = billing;
  return (
    <div className="space-y-6" data-testid="india-billing">
      {billing.demo && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block">Sample billing data — subscribe to a plan below to generate real Razorpay payments & GST invoices.</p>}
      {subscription && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 card-shadow flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">Subscription</p>
            <p className="font-heading text-lg font-bold text-slate-900">{subscription.name}</p>
            <p className="text-sm text-slate-500">Status: <span className="text-emerald-600 font-semibold">{subscription.status}</span> · Renews {subscription.renews_on}</p>
          </div>
          <p className="font-heading text-2xl font-extrabold text-brand">{INR(subscription.amount)}<span className="text-sm text-slate-400">/mo</span></p>
        </div>
      )}

      {/* Subscribe / pay with Razorpay */}
      {plans && plans.length > 0 && (
        <div data-testid="india-plans">
          <h3 className="font-heading font-bold text-slate-900 mb-3">Subscribe with UPI / cards (Razorpay)</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {plans.map((p) => (
              <div key={p.id} className="bg-white border border-slate-200 rounded-2xl p-5 card-shadow flex flex-col">
                <p className="font-heading font-bold text-slate-900">{p.name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{p.tagline}</p>
                <p className="font-heading text-2xl font-extrabold text-slate-900 mt-3">{INR(p.price_amount)}<span className="text-sm text-slate-400">/mo</span></p>
                <p className="text-[11px] text-slate-400">+ 18% GST</p>
                <button onClick={() => subscribe(p)} disabled={!!paying} data-testid={`india-subscribe-${p.id}`}
                  className="mt-4 bg-brand text-white font-semibold rounded-xl px-4 py-2.5 inline-flex items-center justify-center gap-2 disabled:opacity-60 hover:-translate-y-0.5 transition-transform">
                  {paying === p.id ? <Loader2 className="animate-spin" size={16} /> : <><CreditCard size={15} /> Subscribe (auto-pay)</>}
                </button>
                <button onClick={() => pay(p)} disabled={!!paying} data-testid={`india-pay-${p.id}`}
                  className="mt-2 text-xs font-semibold text-slate-500 hover:text-brand">or pay once</button>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Auto-pay renews monthly (up to 12 months) via Razorpay. Test mode — use test UPI <span className="font-mono">success@razorpay</span> or card <span className="font-mono">4111 1111 1111 1111</span>.</p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-shadow">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr><th className="text-left p-3">Invoice</th><th className="text-left p-3">Date</th><th className="text-right p-3">Subtotal</th><th className="text-right p-3">GST 18%</th><th className="text-right p-3">Total</th><th className="text-left p-3">Status</th><th className="text-right p-3">PDF</th></tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-slate-100" data-testid="india-invoice-row">
                <td className="p-3 font-mono text-xs text-slate-700">{inv.number || inv.id}<div className="text-slate-400">{inv.description}{inv.real ? " · paid via Razorpay" : ""}</div></td>
                <td className="p-3 text-slate-600">{inv.date}</td>
                <td className="p-3 text-right text-slate-600">{INR(inv.subtotal)}</td>
                <td className="p-3 text-right text-slate-600">{INR(inv.gst)}</td>
                <td className="p-3 text-right font-semibold text-slate-900">{INR(inv.total)}</td>
                <td className="p-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${inv.status === "paid" ? "text-green-700 bg-green-50" : "text-amber-700 bg-amber-50"}`}>{inv.status}</span></td>
                <td className="p-3 text-right">
                  <button onClick={() => downloadInvoice(inv)} data-testid="india-invoice-pdf" className={`inline-flex items-center gap-1 text-xs font-semibold ${inv.real ? "text-brand hover:underline" : "text-slate-300"}`}><Download size={13} /> PDF</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">{gst_note}</p>
    </div>
  );
}

function EnquiriesTab({ leads }) {
  if (leads === null) return <Loader2 className="animate-spin text-brand mx-auto" size={24} />;
  if (!leads.length) return <p className="text-slate-500 text-center py-16">No enquiries yet. When visitors contact you through your site, they'll appear here.</p>;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-shadow" data-testid="india-enquiries">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
          <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Contact</th><th className="text-left p-3">Status</th><th className="text-left p-3">When</th></tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} className="border-t border-slate-100" data-testid="india-enquiry-row">
              <td className="p-3 font-semibold text-slate-800">{l.name || "—"}</td>
              <td className="p-3 text-slate-600">{l.email || l.phone || "—"}</td>
              <td className="p-3 text-slate-600">{l.status || "new"}</td>
              <td className="p-3 text-slate-400 text-xs">{(l.created_at || "").slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
